<script setup lang="ts">
import { shallowRef } from 'vue'

import AppFrame from '@/components/layout/AppFrame.vue'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { useWorkerAuth } from '@/composables/useWorkerAuth'
import { WorkerLoginError } from '@/services/workerApi'

const username = shallowRef('')
const password = shallowRef('')
const isSubmitting = shallowRef(false)
const errorMessage = shallowRef<string | undefined>()

const { login } = useWorkerAuth()

async function submit() {
  if (isSubmitting.value) return
  isSubmitting.value = true
  errorMessage.value = undefined
  try {
    await login(username.value, password.value)
    password.value = ''
  } catch (error) {
    errorMessage.value =
      error instanceof WorkerLoginError && error.kind === 'credentials'
        ? 'The username or password is incorrect.'
        : 'The Worker is unavailable. Check your connection and try again.'
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <AppFrame>
    <div class="flex h-full min-h-0 flex-col">
      <header
        class="flex h-14 shrink-0 items-center border-b bg-background px-4 pt-[env(safe-area-inset-top)]"
      >
        <h1 class="min-w-0 flex-1 truncate text-base font-bold">Hanoi Bus Atlas</h1>
      </header>
      <main class="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <div class="flex min-h-full items-center justify-center px-4 py-8">
          <Card class="w-full max-w-sm">
            <CardHeader>
              <CardTitle>Sign in to Hanoi Bus Atlas</CardTitle>
              <CardDescription
                >Enter your Worker credentials to load live bus data.</CardDescription
              >
            </CardHeader>
            <CardContent>
              <form class="grid gap-4" @submit.prevent="submit">
                <Alert v-if="errorMessage" variant="destructive">
                  <AlertTitle>Sign-in failed</AlertTitle>
                  <AlertDescription>{{ errorMessage }}</AlertDescription>
                </Alert>
                <FieldGroup>
                  <Field>
                    <FieldLabel for="worker-username">Username</FieldLabel>
                    <Input
                      id="worker-username"
                      v-model="username"
                      autocomplete="username"
                      :disabled="isSubmitting"
                      required
                    />
                  </Field>
                  <Field>
                    <FieldLabel for="worker-password">Password</FieldLabel>
                    <Input
                      id="worker-password"
                      v-model="password"
                      type="password"
                      autocomplete="current-password"
                      :disabled="isSubmitting"
                      required
                    />
                  </Field>
                </FieldGroup>
                <Button type="submit" :disabled="isSubmitting">
                  <Spinner v-if="isSubmitting" class="mr-2" />
                  {{ isSubmitting ? 'Signing in…' : 'Sign in' }}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  </AppFrame>
</template>
